import React, { useEffect, useRef, useState } from "react";

import { COPY } from "../i18n/storefrontCopy";
import { trackStorefront } from "../i18n/StorefrontLocale";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Book waitlist, in place on the book card: name, email and one button.
 * After a successful signup the form is replaced by a confirmation in the same spot.
 */
export default function WaitlistForm({ lang }) {
  const t = COPY[lang].waitlist;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [showError, setShowError] = useState(false);
  const [done, setDone] = useState(false);

  const formRef = useRef(null);
  const emailRef = useRef(null);
  const doneRef = useRef(null);
  const started = useRef(false);

  // Funnel step 2: the visitor starts filling in the form (first focus, once per visit).
  const onStart = () => {
    if (started.current) return;
    started.current = true;
    trackStorefront("storefront_waitlist_started", { lang });
  };

  // Funnel step 2a: the form actually scrolled into view (on phones it sits
  // below the fold), so "never saw it" can be told apart from "saw it, skipped it".
  useEffect(() => {
    const node = formRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        trackStorefront("storefront_waitlist_seen", { lang });
        observer.disconnect();
      }
    }, { threshold: 0.6 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Move focus to the confirmation so keyboard and screen-reader users hear it.
  useEffect(() => {
    if (done) doneRef.current?.focus();
  }, [done]);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const emailValue = email.trim();
    if (!EMAIL_RE.test(emailValue)) {
      // Friction signal: tried to sign up but the email was rejected.
      trackStorefront("storefront_waitlist_error", { lang, reason: emailValue ? "invalid" : "empty" });
      setShowError(true);
      emailRef.current?.focus();
      return;
    }
    setShowError(false);
    // Funnel step 3. Same pattern as the existing offers: the signup is recorded
    // as an analytics event.
    trackStorefront("storefront_waitlist_submitted", {
      lang,
      name: name.trim(),
      email: emailValue,
      has_name: Boolean(name.trim()),
    });
    setDone(true);
  };

  if (done) {
    return (
      <div className="sf-waitlist-done" role="status">
        <div className="sf-done-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m5 12 5 5 9-10" />
          </svg>
        </div>
        <div>
          <h3 ref={doneRef} tabIndex={-1} className="sf-waitlist-done__title">{t.doneTitle}</h3>
          <p className="sf-waitlist-done__text">{t.doneText}</p>
        </div>
      </div>
    );
  }

  return (
    <form ref={formRef} className="sf-waitlist" noValidate onSubmit={handleSubmit} aria-label={t.formLabel}>
      <label className="sf-sr-only" htmlFor="sf-waitlist-name">{t.nameLabel}</label>
      <input
        id="sf-waitlist-name"
        className="sf-input"
        name="name"
        type="text"
        autoComplete="name"
        placeholder={t.namePlaceholder}
        value={name}
        onFocus={onStart}
        onChange={(ev) => setName(ev.target.value)}
      />

      <label className="sf-sr-only" htmlFor="sf-waitlist-email">{t.emailLabel}</label>
      <input
        ref={emailRef}
        id="sf-waitlist-email"
        className="sf-input"
        name="email"
        type="email"
        autoComplete="email"
        inputMode="email"
        placeholder={t.emailPlaceholder}
        required
        value={email}
        onFocus={onStart}
        onChange={(ev) => setEmail(ev.target.value)}
        aria-invalid={showError || undefined}
        aria-describedby={showError ? "sf-waitlist-error" : undefined}
      />
      <p className="sf-error" id="sf-waitlist-error" hidden={!showError}>
        {t.emailError}
      </p>

      <button type="submit" className="sf-btn">
        {t.submit}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M5 12h14" />
          <path d="m13 6 6 6-6 6" />
        </svg>
      </button>
      <p className="sf-waitlist__note">{t.note}</p>
    </form>
  );
};
